import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageSquare, ShieldCheck, Loader2, CalendarIcon, X, Clock } from "lucide-react";
import { format } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { PHONE_TEL, TIME_WINDOWS } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptySlot = { date: null, window: "" };
const initial = {
  name: "",
  email: "",
  phone: "",
  address: "",
  damage_type: "",
  contact_preference: "",
  message: "",
  slots: [{ ...emptySlot }],
};

const today = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

export const InspectionForm = ({ onDone }) => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateSlot = (idx, key, val) =>
    setForm((f) => ({
      ...f,
      slots: f.slots.map((s, i) => (i === idx ? { ...s, [key]: val } : s)),
    }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["name", "email", "phone", "address", "damage_type", "contact_preference"];
    for (const k of required) {
      if (!form[k] || !String(form[k]).trim()) {
        toast.error(`Please fill in ${k.replace("_", " ")}`);
        return;
      }
    }
    // Validate availability slot
    const s = form.slots[0];
    if (!s.date || !s.window) {
      toast.error("Pick a day and time window for your inspection");
      return;
    }

    const payload = {
      name: form.name,
      email: form.email,
      phone: form.phone,
      address: form.address,
      damage_type: form.damage_type,
      contact_preference: form.contact_preference,
      message: form.message,
      availability: form.slots.map((s) => ({
        date: format(s.date, "yyyy-MM-dd"),
        window: s.window,
      })),
    };

    setSubmitting(true);
    try {
      await axios.post(`${API}/inspections`, payload);
      toast.success("Inspection request sent — JD will confirm soon.");
      setDone(true);
      setForm(initial);
      if (onDone) onDone();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send. Please call or text instead.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="form-success"
        className="bg-slate-900/70 border border-red-600/40 p-10 text-center"
      >
        <ShieldCheck className="text-red-600 mx-auto" size={48} />
        <h3 className="font-display uppercase font-black text-2xl text-white mt-4">
          Request received
        </h3>
        <p className="text-slate-300 mt-3 max-w-md mx-auto">
          Thanks. JD will pick one of the 3 windows you provided and confirm by your preferred
          contact method. Inspections take about 2 hours.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
          <a
            data-testid="success-call"
            href={`tel:${PHONE_TEL}`}
            className="bg-red-600 text-white font-bold uppercase tracking-wide hover:bg-red-500 px-6 py-3 inline-flex items-center justify-center gap-2"
          >
            <Phone size={16} /> Call JD now
          </a>
          <button
            data-testid="submit-another"
            onClick={() => setDone(false)}
            className="bg-transparent border-2 border-slate-700 text-white font-bold uppercase tracking-wide hover:border-red-600 hover:text-red-600 px-6 py-3"
          >
            Submit another
          </button>
        </div>
      </div>
    );
  }

  const inputCls =
    "w-full bg-white border-2 border-slate-300 text-slate-900 font-semibold px-4 py-3 placeholder:text-slate-400 placeholder:font-normal focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-red-600 hover:border-slate-400 transition";

  return (
    <form
      data-testid="inspection-form"
      onSubmit={submit}
      className="relative bg-black border-2 border-red-600 p-6 md:p-10 shadow-[0_20px_60px_-15px_rgba(220,38,38,0.45)]"
    >
      {/* Red accent top bar */}
      <div className="absolute inset-x-0 top-0 h-1 bg-red-600" />
      {/* Corner accents */}
      <div className="absolute -top-px -left-px w-12 h-12 border-t-2 border-l-2 border-sky-400" />
      <div className="absolute -top-px -right-px w-12 h-12 border-t-2 border-r-2 border-sky-400" />
      <div className="absolute -bottom-px -left-px w-12 h-12 border-b-2 border-l-2 border-sky-400" />
      <div className="absolute -bottom-px -right-px w-12 h-12 border-b-2 border-r-2 border-sky-400" />

      <div className="text-xs uppercase tracking-[0.3em] text-sky-300 mb-2">
        Free Inspection Request
      </div>
      <h3 className="font-impact uppercase text-4xl sm:text-5xl text-white tracking-tight">
        Tell JD about your roof
      </h3>
      <div className="h-1 w-16 bg-red-600 mt-3 mb-3" />
      <p className="text-slate-200 text-sm mt-2 mb-8">
        No pressure, no obligation. JD verifies claim eligibility for wind, hail, and tree damage.
      </p>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-sky-300 font-bold mb-1 block">Full name</label>
          <input
            data-testid="input-name"
            className={inputCls}
            placeholder="Jane Homeowner"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-sky-300 font-bold mb-1 block">Phone</label>
          <input
            data-testid="input-phone"
            type="tel"
            className={inputCls}
            placeholder="(404) 555-0123"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-sky-300 font-bold mb-1 block">Email</label>
          <input
            data-testid="input-email"
            type="email"
            className={inputCls}
            placeholder="you@email.com"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-sky-300 font-bold mb-1 block">Address</label>
          <input
            data-testid="input-address"
            className={inputCls}
            placeholder="123 Peach St, Atlanta GA"
            value={form.address}
            onChange={(e) => update("address", e.target.value)}
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-sky-300 font-bold mb-1 block">Damage type</label>
          <Select value={form.damage_type} onValueChange={(v) => update("damage_type", v)}>
            <SelectTrigger
              data-testid="select-damage"
              className="w-full bg-white border-2 border-slate-300 text-slate-900 font-semibold px-4 py-3 h-auto rounded-none focus:ring-2 focus:ring-red-600 focus:border-red-600 hover:border-slate-400"
            >
              <SelectValue placeholder="Select damage type" />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-300 text-slate-900 rounded-none">
              <SelectItem value="wind">Wind</SelectItem>
              <SelectItem value="hail">Hail</SelectItem>
              <SelectItem value="tree">Tree / Fallen limb</SelectItem>
              <SelectItem value="other">Other / Not sure</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-sky-300 font-bold mb-1 block">Preferred contact</label>
          <Select value={form.contact_preference} onValueChange={(v) => update("contact_preference", v)}>
            <SelectTrigger
              data-testid="select-contact"
              className="w-full bg-white border-2 border-slate-300 text-slate-900 font-semibold px-4 py-3 h-auto rounded-none focus:ring-2 focus:ring-red-600 focus:border-red-600 hover:border-slate-400"
            >
              <SelectValue placeholder="How should JD reach you?" />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-300 text-slate-900 rounded-none">
              <SelectItem value="call">Phone call</SelectItem>
              <SelectItem value="text">Text message</SelectItem>
              <SelectItem value="email">Email</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Availability */}
      <div className="mt-10 pt-8 border-t border-slate-700">
        <div className="text-xs uppercase tracking-[0.3em] text-sky-300 mb-2">
          Pick a time · Required
        </div>
        <h4 className="font-display uppercase font-bold text-xl text-white">
          When works for your inspection?
        </h4>
        <div className="mt-2 flex items-center gap-2 text-sky-300 text-sm bg-sky-500/10 border border-sky-500/30 px-3 py-2">
          <Clock size={14} />
          <span className="font-bold">Plan for a 2-hour inspection window.</span>
        </div>
        <p className="text-slate-400 text-sm mt-4 mb-6">
          Pick one day and a 2-hour window that works best. JD will confirm by your preferred
          contact method.
        </p>

        <div className="space-y-4">
          {form.slots.map((slot, idx) => (
            <div
              key={idx}
              data-testid={`slot-${idx}`}
              className="grid sm:grid-cols-2 gap-3 items-center bg-slate-900/60 border border-slate-700 p-4"
            >
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid={`slot-${idx}-date-btn`}
                    className={
                      "w-full bg-white border-2 border-slate-300 text-left px-4 py-3 inline-flex items-center gap-2 hover:border-red-600 transition-colors font-semibold " +
                      (slot.date ? "text-slate-900" : "text-slate-400 font-normal")
                    }
                  >
                    <CalendarIcon size={16} className="text-red-600" />
                    {slot.date ? format(slot.date, "EEE, MMM d") : "Pick a day"}
                    {slot.date && (
                      <span
                        role="button"
                        tabIndex={0}
                        aria-label="Clear date"
                        className="ml-auto text-slate-500 hover:text-white cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          updateSlot(idx, "date", null);
                        }}
                      >
                        <X size={14} />
                      </span>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white border-slate-300 rounded-none" align="start">
                  <Calendar
                    mode="single"
                    selected={slot.date}
                    onSelect={(d) => updateSlot(idx, "date", d)}
                    disabled={(d) => d < today()}
                    initialFocus
                    className="text-slate-900"
                  />
                </PopoverContent>
              </Popover>

              <Select value={slot.window} onValueChange={(v) => updateSlot(idx, "window", v)}>
                <SelectTrigger
                  data-testid={`slot-${idx}-window`}
                  className="w-full bg-white border-2 border-slate-300 text-slate-900 font-semibold px-4 py-3 h-auto rounded-none focus:ring-2 focus:ring-red-600 focus:border-red-600 hover:border-slate-400"
                >
                  <SelectValue placeholder="Pick a 2-hour window" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-300 text-slate-900 rounded-none">
                  {TIME_WINDOWS.map((w) => (
                    <SelectItem key={w} value={w}>
                      {w}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8">
        <label className="text-xs uppercase tracking-widest text-sky-300 font-bold mb-1 block">
          Anything else? (optional)
        </label>
        <textarea
          data-testid="input-message"
          rows={4}
          className={inputCls + " resize-none"}
          placeholder="Recent storm date, visible damage, insurance status…"
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
        />
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <button
          data-testid="submit-inspection"
          type="submit"
          disabled={submitting}
          className="bg-red-600 text-white font-bold uppercase tracking-wide hover:bg-red-500 disabled:opacity-60 px-8 py-4 inline-flex items-center justify-center gap-2 transition-colors"
        >
          {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
          {submitting ? "Sending…" : "Request free inspection"}
        </button>
        <a
          data-testid="form-text-fallback"
          href={`sms:${PHONE_TEL}`}
          className="bg-transparent border-2 border-slate-700 text-white font-bold uppercase tracking-wide hover:border-red-600 hover:text-red-600 px-8 py-4 inline-flex items-center justify-center gap-2 transition-colors"
        >
          <MessageSquare size={16} /> Text instead
        </a>
      </div>
      <p className="text-xs text-slate-500 mt-4">
        By submitting, you agree to be contacted by JD Dean about your roof inspection. No spam.
      </p>
    </form>
  );
};
